import { Fragment, useCallback, useMemo } from 'react';
import hand from '~/assets/icons/hand.svg';
import { playSound, Sound } from '~/assets/sounds';
import { Text } from '~/components/ui/Text';
import { rage } from '~/rage';
import { formatPromocodeTime } from '../../utils';
import { QuestPromocodeItem } from '../QuestPromocodeItem';
import { IQuestPromocodeProps, spanMap } from './QuestPromocode.model';
import {
	StyledCompleteContainer,
	StyledContainer,
	StyledItemsContainer,
	StyledLeftContainer,
	StyledLeftTextContainer,
	StyledProgressBg,
	StyledProgressBgWrapper,
	StyledProgressContainer,
	StyledProgressTextContainer,
	StyledRightContainer,
	StyledShadow,
} from './QuestPromocode.styled';

const QuestPromocode = ({
	name,
	code,
	description,
	currentCount,
	targetCount,
	currentTime,
	targetTime,
	complete,
	rewards,
}: IQuestPromocodeProps) => {
	const getSpan = (rewardsLength: number, index: number) => {
		const spans = spanMap[rewardsLength as keyof typeof spanMap];
		if (!spans || !spans[index]) {
			return { rowSpan: 1, colSpan: 1 };
		}
		return spans[index];
	};

	const { current, target, unit } = useMemo(
		() => formatPromocodeTime(currentTime || 0, targetTime || 0),
		[currentTime, targetTime],
	);

	const handleClaim = useCallback(async () => {
		if (!complete) {
			return;
		}

		await rage.promo.claimRewards(code);
	}, [code, complete]);

	return (
		<StyledContainer $complete={complete}>
			<StyledShadow $complete={complete} />
			<StyledLeftContainer>
				<StyledLeftTextContainer>
					<Text style={{ fontSize: 16 }}>
						<span style={{ fontWeight: 500 }}>Промокод</span>{' '}
						<span style={{ fontWeight: 700 }}>{name}</span>
					</Text>
					<Text style={{ opacity: 0.5, fontSize: 14 }}>{description}</Text>
				</StyledLeftTextContainer>
				<StyledProgressContainer>
					<StyledProgressTextContainer $complete={complete}>
						<Text>{complete ? 'Выполнено' : 'Прогресс выполнения'}</Text>
						<Text>
							{currentCount && targetCount && currentCount > 0 ? (
								<Fragment>
									{currentCount?.toLocaleString('ru-RU')}{' '}
									<span>/ {targetCount?.toLocaleString('ru-RU')}</span>
								</Fragment>
							) : (
								<Fragment>
									{current}{' '}
									<span>
										/ {target} {unit}
									</span>
								</Fragment>
							)}
						</Text>
					</StyledProgressTextContainer>
					<StyledProgressBgWrapper>
						<StyledProgressBg
							$complete={complete}
							style={{
								width: `${
									(() => {
										if (currentCount && targetCount && currentCount > 0) {
											if (targetCount === 0) {
												return 1;
											}

											return Math.min(1, currentCount / targetCount);
										}

										if (targetTime === 0) {
											return 1;
										}

										if (currentTime && targetTime && currentTime > 0) {
											return Math.min(1, currentTime / targetTime);
										}

										return 0;
									})() * 100
								}%`,
							}}
						/>
					</StyledProgressBgWrapper>
				</StyledProgressContainer>
			</StyledLeftContainer>
			<StyledRightContainer $complete={complete}>
				<StyledItemsContainer $complete={complete}>
					{rewards.slice(0, 4).map((item, index) => (
						<QuestPromocodeItem
							item={item}
							key={index}
							rowSpan={getSpan(Math.min(rewards.length, 4), index).rowSpan}
							colSpan={getSpan(Math.min(rewards.length, 4), index).colSpan}
						/>
					))}
					<Text style={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.5)' }}>
						{targetCount?.toLocaleString('ru-RU')}
					</Text>
				</StyledItemsContainer>
				<StyledCompleteContainer
					onClick={() => {
						handleClaim();
						playSound(Sound.ClickButton);
					}}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
					$complete={complete}
				>
					<img src={hand} />
				</StyledCompleteContainer>
			</StyledRightContainer>
		</StyledContainer>
	);
};

export { QuestPromocode };
